import * as React from 'react';
import { Table, Column } from 'fixed-data-table-2';

import { Binding } from 'core/infrastructure';
import { deepEquals, DateUtils, Environment } from 'core/utils';

import * as ResizeListener from './ResizeListener';

import * as StatePersister from './DataGrid.StatePersister';
import { SortDirection, createHeader } from './DataGrid.SortHeaderCell';
import { createCell } from './DataGrid.DefaultCell';
import { createSelectorColumn } from './DataGrid.SelectorColumn';
import { DataGridColumn, applyColumnFormat } from './DataGrid.Column';
import { ColumnFilter } from './DataGrid.Filter';
import { filterAndSort } from './DataGrid.Utils';
import { getNewSelection, translateKeyCodeToRowIndex } from './DataGrid.selection';
import { saveDataToExcel, printData } from './DataGrid.ExcelExport';
import { configureFilter } from './DataGrid.FilterDialog';
import { createFooter } from './DataGrid.FooterCell';

const DEFAULT_ROW_HEIGHT = 32;
const DEFAULT_HEADER_HEIGHT = 36;
const DEFAULT_FOOTER_HEIGHT = 36;

interface Props<T> extends React.HTMLProps<DataGrid<T>> {
	id: string;
	columns: DataGridColumn<T>[];
	initialData: T[];

	// Valor usado para comparar items (p.ej. person => person.Id)
	// Para comparar dos items, se hará un deepEquals sobre este valor
	itemKey: (item: T) => any;

	filtrable?: boolean;
	multiSelect?: boolean;
	sortable?: boolean;
	initialColumnFilter?: ColumnFilter<T>[];
	initialSortBy?: Binding.PropertyPath<T>;
	initialSortDirection?: SortDirection;
	onSelectedItemsChanged?: (selectedItems: T[]) => void;

	// Al recargar estado, se puede restablecer un searchText. Doy la oportunidad de que
	// los usuarios de la clase puedan hacer algo (como pintar el texto en un input)
	onSearchTextChanged?: (searchText: string) => void;

	onSortChanged?: (sortedData: T[]) => void;
	onFilteredItemsChanged?: () => void;

	// Esto nos permite que al cambiar los items del grid, no se mantenga la ordenación. Es necesario
	// si estamos usándolo en un componente que permite reordenar desde fuera
	keepSortAfterOnPropsChanged?: boolean;

	// Ancho en pixels de la tabla. Si no se especifica ancho en pixels, se tomará el del padre
	width?: number;

	height?: number;
	maxHeight?: number;
	headerHeight?: number;

	// Permite indicar que la altura de la columnas se calculará dinámicamente (penaliza el rendimiento)
	variableRowHeight?: boolean;
	rowHeight?: number;
	rowHeightGetter?: (index: number) => number

	onStateLoaded?: () => void;
	shouldPersistState?: boolean;
	getRowClassName?: (item: T) => string;
	hideExportButton?: boolean;
}

interface State<T> {
	data: T[];
	sortBy: Nullable<string>;
	sortDirection: Nullable<SortDirection>;
	selectedItems: T[];
	scrollPosition: { x: number, y: number };
	searchText: string;
	columnFilter: ColumnFilter<T>[];

	// Anchura. Puede haber sido calculada dinámicamente a partir del padre
	width: number;
	columnWidths: { [columnKey: string]: number };

	// Flag para saber si es neecesario intentar recuperar estado persistido o no
	stateLoaded: boolean;
}

function itemEquals<T>(item1: T, item2: T, getItemKey: (item: T) => any) {
	return deepEquals(getItemKey(item1), getItemKey(item2));
}

function getValidSelection<T>(currentSelection: T[], newData: T[], getItemKey: (item: T) => any) {
	return newData.filter(s => currentSelection.some(d => itemEquals(d, s, getItemKey)));
}

function shouldPersistState<T>(props: Props<T>) {
	return typeof props.shouldPersistState === 'undefined' || props.shouldPersistState;
}

function createStateFromProps<T>(nextProps: Props<T>, prevState: State<T>): Partial<State<T>> {
	// Hago lo que se hacía en el componentWillReceiveProps
	// La idea es que me han cambiado los props y tengo que mezclar mi estado con los nuevos props
	// El initialColumnFilter *nunca* lo actualizo. Es inicial y sólo se tendrá en cuenta cuando se cargue
	// el componente la primera vez.

	const keepSort = typeof nextProps.keepSortAfterOnPropsChanged === 'undefined' || nextProps.keepSortAfterOnPropsChanged;
	const { searchText, columnFilter } = prevState;
	let { sortBy, sortDirection } = prevState;
	sortBy = keepSort ? sortBy : null;
	sortDirection = keepSort ? sortDirection : null;
	const newData = filterAndSort(nextProps.initialData, nextProps.columns, searchText, columnFilter, sortBy, sortDirection);
	const newSelection = getValidSelection(prevState.selectedItems, newData, nextProps.itemKey);

	return {
		data: newData,
		selectedItems: newSelection,
		// Si nos fijan un ancho por props, lo usamos. Si no, mantenemos el que estuviera calculado
		width: nextProps.width || prevState.width,
	};
}

function createRestoredState<T>(nextProps: Props<T>, prevState: State<T>): Partial<State<T>> {
	// Hago lo que se hacía en el componentWillMount
	// La idea es que estoy haciendo la primera carga del componente y necesito mezclar los props
	// con mi estado o, tal vez, con el estado que hay guardado en disco

	const loadedState = StatePersister.load(nextProps.id, nextProps.columns);

	if (shouldPersistState(nextProps) && loadedState) {

		const { sortBy, sortDirection, selectedKeys, searchText, scrollPosition, columnWidths } = loadedState;
		let { columnFilter } = loadedState;
		columnFilter = nextProps.initialColumnFilter || columnFilter;

		const newData = filterAndSort(nextProps.initialData, nextProps.columns, searchText, columnFilter, sortBy, sortDirection);
		const selectedItems = selectedKeys
			.map(key => newData.find(item => deepEquals(nextProps.itemKey(item), key))!)
			.filter(x => x); // Desecho los que no se han encontrado ya

		return {
			data: newData,
			selectedItems,
			sortDirection,
			sortBy,
			scrollPosition,
			searchText,
			columnWidths: columnWidths || {}, // Por si acaso viene de una versión anterior
			columnFilter: columnFilter,
			stateLoaded: true,
		};
	}
	else {
		const data = filterAndSort(nextProps.initialData, nextProps.columns, prevState.searchText, prevState.columnFilter, prevState.sortBy, prevState.sortDirection);
		return {
			data,
			stateLoaded: true
		};
	}
}

export class DataGrid<T> extends React.Component<Props<T>, State<T>> {

	private mounted = false;
	private observer: Nullable<MutationObserver> = null;
	private ref = React.createRef<HTMLDivElement>();

	constructor(props: Props<T>) {
		super(props);
		this.state = {
			data: [],
			columnFilter: props.initialColumnFilter ? props.initialColumnFilter : [],
			sortBy: this.props.initialSortBy ? Binding.propertyPathToString(this.props.initialSortBy) : null,
			sortDirection: this.props.initialSortDirection || null,
			selectedItems: [],
			scrollPosition: { x: 0, y: 0 },
			searchText: '',
			width: props.width || 600,
			columnWidths: {},
			stateLoaded: false
		};

		this.onUpdateWidth = this.onUpdateWidth.bind(this);
		this.onRowClick = this.onRowClick.bind(this);
		this.onScrollEnd = this.onScrollEnd.bind(this);
		this.onSortChange = this.onSortChange.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onColumnResize = this.onColumnResize.bind(this);
	}

	static getDerivedStateFromProps<T>(nextProps: Props<T>, prevState: State<T>): Partial<State<T>> {
		return prevState.stateLoaded
			? createStateFromProps(nextProps, prevState)
			: createRestoredState(nextProps, prevState);
	}


	private calculateWidth(props: Props<T>) {
		if (!this.mounted)
			return props.width || 0;

		return props.width || $(this.ref.current!).parent().width();
	}

	componentWillUnmount() {
		this.mounted = false;
		const parent = this.ref.current!.parentNode as Element;
		ResizeListener.remove(parent);
		if (this.observer)
			this.observer.disconnect();
	}

	private onUpdateWidth() {
		if (this.mounted) {
			// Optimizo el refresco porque eso se puede llamar muchas veces
			const newWidth = this.calculateWidth(this.props);
			const currentWidth = this.state.width;
			if (newWidth !== currentWidth) {
				const width = this.calculateWidth(this.props);
				if (width !== undefined)
					this.setState({ width });
			}
		}
	}

	setupWidthObservers(): void {
		const parent = this.ref.current!.parentNode as Element;
		ResizeListener.add(parent as Element, this.onUpdateWidth);

		// Necesito observar cambios (y además a lo bestia) por escenarios raros,
		// como cuando arranca el grid dentro de un panel collapsado, y su parent
		// no tiene tamaño definido todavía por no ser visible.
		this.observer = new MutationObserver(() => {
			this.onUpdateWidth();
		});

		this.observer.observe(document.body, { attributes: true, subtree: true });

		// ¿Por qué hace falta esto? Ni idea. En teoría, con ponerlo ahora y además engancharme al evento
		// de Resize del padre debería bastar, pero en el caso de los diálogos no es suficiente, así que
		// programo otra actualización para el siguiente ciclo de repintado
		setTimeout(() => {
			if (this.mounted) {
				const width = this.calculateWidth(this.props);
				if (width !== undefined)
					this.setState({ width });
			}


			// Esto se llamaba antes en el componentWillMount en la callback de setState.
			// Necesito llamarlo en un setTimeout porque las callback pueden querer acceder
			// al grid imperativamente a través de una referencia, y hasta que no está montado
			// el componente no se tiene acceso a ella.
			// Un ejemplo sería: onFilteredItemsChanged = () => this.grid!.getFilteredAndSortedItems()....

			if (this.props.onSearchTextChanged)
				this.props.onSearchTextChanged(this.state.searchText || '');

			if (this.props.onStateLoaded)
				this.props.onStateLoaded();

			if (this.props.onFilteredItemsChanged)
				this.props.onFilteredItemsChanged();

		}, 0);
	}

	componentDidMount(): void {
		this.mounted = true;
		this.addToolsButtons();
		this.setupWidthObservers();
	}

	componentDidUpdate(_prevProps: Props<T>, _prevState: State<T>) {
		if (shouldPersistState(this.props)) {
			StatePersister.save(this.props.id, {
				sortBy: this.state.sortBy,
				sortDirection: this.state.sortDirection,
				selectedKeys: this.state.selectedItems.map(this.props.itemKey),
				searchText: this.state.searchText,
				scrollPosition: this.state.scrollPosition,
				columnWidths: this.state.columnWidths,
				columnFilter: this.state.columnFilter
			});
		}

		const selectionChanged =
			this.state.selectedItems.length !== _prevState.selectedItems.length
			|| this.state.selectedItems.except(_prevState.selectedItems).length > 0
			|| _prevState.selectedItems.except(this.state.selectedItems).length > 0;

		if (selectionChanged && this.props.onSelectedItemsChanged)
			this.props.onSelectedItemsChanged(this.state.selectedItems);

		// Aquí no puede ser muy listo y detectar si hubo cambios o no. Hasta ahora se
		// llama siempre en el componentWillReceiveProps y no había sido grave.
		if (this.props.onFilteredItemsChanged)
			this.props.onFilteredItemsChanged();

		if (this.state.columnFilter.length > 0)
			$('#filter-button').removeClass('btn-outline');
		else
			$('#filter-button').addClass('btn-outline').blur();
	}

	withSelected(action: (item: T[]) => void) {
		if (this.state.selectedItems.length)
			action(this.state.selectedItems);
	}

	getSelectedItems(): T[] {
		return this.state.selectedItems;
	}

	getCurrentFilter(): ColumnFilter<T>[] {
		return this.state.columnFilter;
	}

	getFilteredAndSortedItems(): T[] {
		return this.state.data;
	}

	selectItems(items: T[]): void {
		this.setState({ selectedItems: items });
	}

	// Permite gestionar una pulsación de tecla generada en un control externo, por
	// ejemplo un input de filtro/búsqueda para cambiar la selección del grid si
	// se pulsan las flechas de arriba y abajo. Devuelve true si el evento ya ha sido
	// gestionado, y false si el grid no ha hecho nada con él.
	handleExternalKeyDown(e: React.KeyboardEvent<unknown>): boolean {
		if (e.key === 'ArrowUp') {
			this.selectPreviousItem();
			e.preventDefault();
			return true;
		}

		if (e.key === 'ArrowDown') {
			this.selectNextItem();
			e.preventDefault();
			return true;
		}

		return false;
	}

	selectFirstItem(): void {
		this.setState({ selectedItems: this.state.data.length ? [this.state.data[0]] : [] });
	}

	selectNextItem(): void {
		const selected = this.state.selectedItems;
		const all = this.state.data;
		if (!selected.length) {
			this.selectFirstItem();
		}
		else {
			const index = Math.min(all.indexOf(selected[0]) + 1, all.length - 1);
			this.selectItems([all[index]]);
		}
	}

	selectPreviousItem(): void {
		const selected = this.state.selectedItems;
		const all = this.state.data;
		if (!selected.length) {
			this.selectFirstItem();
		}
		else {
			const index = Math.max(all.indexOf(selected[0]) - 1, 0);
			this.selectItems([all[index]]);
		}
	}

	filterBy(searchText: string) {
		const data = filterAndSort(this.props.initialData, this.props.columns, searchText, this.state.columnFilter, this.state.sortBy, this.state.sortDirection);
		const selectedItems = getValidSelection(this.state.selectedItems, data, this.props.itemKey);
		this.setState({ data, selectedItems, searchText });
	}

	clearSort() {
		this.setState({ sortBy: null, sortDirection: null });
	}

	private onRowClick(e: React.SyntheticEvent<Table>, rowIndex: number): void {
		const newSelection = getNewSelection({
			allItems: this.state.data,
			selectedItems: this.state.selectedItems,
			multiSelect: !!this.props.multiSelect,
			clickedRowIndex: rowIndex,
			ctrlKey: (e.nativeEvent as MouseEvent).ctrlKey,
			shiftKey: (e.nativeEvent as MouseEvent).shiftKey,
		});

		this.setState({ selectedItems: newSelection });
	}

	private onScrollEnd(scrollX: number, scrollY: number): void {

		this.setState({ scrollPosition: { x: scrollX, y: scrollY } });
	}

	private isSelected(item: T) {
		return item && this.state.selectedItems.some(selected => itemEquals(item, selected, this.props.itemKey));
	}

	private onSortChange(sortBy: string, sortDirection: SortDirection) {
		if (this.props.sortable || typeof this.props.sortable === 'undefined') {
			const data = filterAndSort(this.props.initialData, this.props.columns, this.state.searchText, this.state.columnFilter, sortBy, sortDirection);
			this.setState({
				sortBy,
				sortDirection,
				data
			}, () => this.props.onSortChanged && this.props.onSortChanged(data));
		}
	}

	private createSelectionColumn() {
		return createSelectorColumn({
			allItems: this.state.data,
			selectedItems: this.state.selectedItems,
			isSelected: x => this.isSelected(x), // OJO, que necesitas el => por el bind a this
			setSelectedItems: selectedItems => this.setState({ selectedItems })
		});
	}

	private onKeyDown(e: React.KeyboardEvent<any>) {
		const rowIndex = translateKeyCodeToRowIndex(this.state.data, this.state.selectedItems, e.keyCode);
		if (!rowIndex.ok)
			return;

		const newSelection = getNewSelection({
			allItems: this.state.data,
			selectedItems: this.state.selectedItems,
			multiSelect: !!this.props.multiSelect,
			clickedRowIndex: rowIndex.value,
			ctrlKey: e.ctrlKey,
			shiftKey: e.shiftKey
		});

		this.setState({ selectedItems: newSelection });
	}

	private addToolsButtons() {
		const container = $('<div class=\'tools-buttons\'></div>');

		if (!this.props.hideExportButton)
			this.addExportButtons(container);
		if (this.props.filtrable)
			this.addFilterButtons(container);

		const table = $(this.ref.current?.children[0]!);
		if (table)
			table.append(container);
	}

	private addFilterButtons(container: JQuery) {
		const filterBtn = $('<button id=\'filter-button\' class=\'btn btn-info btn-outline m-t-xs\' type=\'button\'><i class=\'fa fa-filter\'></i></button>');
		filterBtn.click(async () => {
			const result = await this.configureFilter(this.state.columnFilter, this.props.columns, this.props.initialData);
			if (!result.ok)
				return;

			const columnFilter = result.value;

			const data = filterAndSort(this.props.initialData, this.props.columns, this.state.searchText, columnFilter, this.state.sortBy, this.state.sortDirection);
			this.setState({
				columnFilter,
				data
			});
		});

		container.append(filterBtn);
	}

	protected async configureFilter(currentFilters: ColumnFilter<T>[], columns: DataGridColumn<T>[], items: T[]): Promise<Result<ColumnFilter<T>[]>> {
		return configureFilter(currentFilters, columns, items);
	}

	private addExportButtons(container: JQuery) {

		const downloadExcelBtn = $('<button class=\'btn btn-info btn-outline\' type=\'button\'><i class=\'fa fa-download\'></i></button>');
		downloadExcelBtn.click(() => {
			const now = DateUtils.formatDate(new Date(), 'ddMMyyyy_HHmmss');
			saveDataToExcel(this.state.data, this.props.columns, `data_${now}`);
		});

		const printHtmlBtn = $('<button class=\'btn btn-info btn-outline\' type=\'button\'><i class=\'fa fa-print\'></i></button>');
		printHtmlBtn.click(() => printData(this.state.data, this.props.columns));

		const btnGroup = $('<div class=\'btn-group-vertical\'></div>');

		if (!Environment.isElectron())
			btnGroup.append(printHtmlBtn);

		btnGroup.append(downloadExcelBtn);
		container.append(btnGroup);
	}

	private onColumnResize(newColumnWidth: number, columnKey: string): void {
		this.setState(({ columnWidths }: State<T>) => ({
			columnWidths: {
				...columnWidths,
				[columnKey]: newColumnWidth
			}
		}));
	}

	render() {
		const defaultColWidth = 200;
		// Si ninguna columna tiene puesto flexGrow, reparto el espacio proporcionalmente
		const defaultFlexGrow = this.props.columns.every(x => typeof x.flexGrow === 'undefined') ? 1 : 0;

		const hasToolButtons = this.props.filtrable || !this.props.hideExportButton;

		const getColumnWidth = (col: DataGridColumn<T>) => {
			if (this.state.columnWidths[col.title])
				return this.state.columnWidths[col.title];

			// Si no se ha redimensionado la columna y es la última, añado 50px de ancho para
			// dejar hueco a la barra de scroll y los botones de operación
			const BUTTONS_WIDTH = 50;
			const width = col.width || defaultColWidth;
			const isLastColumn = col === this.props.columns[this.props.columns.length - 1];
			return isLastColumn && hasToolButtons ? width + BUTTONS_WIDTH : width;
		};

		const columns = this.props.columns.filter(x => x.isVisible || typeof x.isVisible === 'undefined').map((column, idx) => <Column key={column.title}
			columnKey={column.title}
			width={getColumnWidth(column)}
			minWidth={15}
			flexGrow={column.flexGrow || defaultFlexGrow}
			allowCellsRecycling={true}
			fixed={column.fixed && this.state.width > 400}
			isResizable={column.isResizable || typeof column.isResizable === 'undefined'}
			header={createHeader(column, this.state.sortBy, this.state.sortDirection, this.onSortChange)}
			footer={createFooter(column, this.state.data, idx === this.props.columns.length - 1)}
			cell={createCell({ column, items: this.state.data, onKeyDown: this.onKeyDown, isLastColumn: idx === this.props.columns.length - 1 })} />);

		if (this.props.multiSelect) {
			columns.unshift(this.createSelectionColumn());
		}

		const rowClassNameGetter = (rowIndex: number) => {
			if (this.isSelected(this.state.data[rowIndex]))
				return 'selected';

			if (this.props.getRowClassName)
				return this.props.getRowClassName(this.state.data[rowIndex]);

			return '';
		};

		// Si hay items seleccionados, aseguro que se vé el último, si no,
		// me voy al primero
		const scrollToRow: number | undefined = this.state.selectedItems.length > 0
			? this.state.data.indexOf(this.state.selectedItems[this.state.selectedItems.length - 1])
			: 0;

		const rowHeightGetter: ((index: number) => number) | undefined = this.props.variableRowHeight === true
			? ((index: number) => {
				const item = this.state.data[index];

				const lines = this.props.columns
					.map(column => applyColumnFormat(column, Binding.getValue(item, Binding.propertyPathToString(column.linkTo)), item))
					.map(formattedValue => formattedValue.split('\n').length)
					.reduce((acc, length) => Math.max(acc, length), 0);

				const PADDING = 14;
				const LINE_HEIGHT = 18;

				return PADDING + LINE_HEIGHT * lines;
			})
			: undefined;

		const footerHeight = this.state.data.length && this.props.columns.some(x => !!x.showTotalizeFooter) ? DEFAULT_FOOTER_HEIGHT : 0;

		return <div ref={this.ref} style={{ display: 'contents' }}>
			<Table
				className={hasToolButtons ? 'has-tool-buttons' : ''}
				rowClassNameGetter={rowClassNameGetter}
				touchScrollEnabled={true}
				scrollLeft={this.state.scrollPosition.x}
				scrollTop={this.state.scrollPosition.y}
				scrollToRow={scrollToRow}
				rowsCount={this.state.data.length}
				headerHeight={this.props.headerHeight || DEFAULT_HEADER_HEIGHT}
				footerHeight={footerHeight}
				rowHeight={this.props.rowHeight || DEFAULT_ROW_HEIGHT}
				rowHeightGetter={this.props.rowHeightGetter || rowHeightGetter}
				height={this.props.height || 400}
				width={this.state.width}
				maxHeight={this.props.maxHeight || 400}
				onColumnResizeEndCallback={this.onColumnResize}
				isColumnResizing={false}
				onRowClick={this.onRowClick}
				onScrollEnd={this.onScrollEnd}>
				{columns}
			</Table>
		</div>;
	}
}

export type DataGridCtor<T> = new (props: Props<T>) => DataGrid<T>;
